import React, { useEffect, useRef, useState } from "react";
import { Bot, Loader2, Send, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const STORAGE_KEY = "autopark_seller_assistant_hist";

const STARTERS = [
  "Melyik hirdetésemen van bekapcsolva az AI ügyfélszolgálat?",
  "Hogyan töltsem ki jól a privát AI briefet?",
  "Mit lát a vevő, amikor az AI válaszol?",
  "Hogyan kapcsolom be az AI-t egy új autón?",
];

function loadHistory() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.slice(-40) : [];
  } catch {
    return [];
  }
}

export function SellerAssistantPanel({ embedded = false }) {
  const [input, setInput] = useState("");
  const [history, setHistory] = useState(() => loadHistory());
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(history.slice(-40)));
  }, [history]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [history, loading]);

  const send = async (textOverride) => {
    const text = (textOverride ?? input).trim();
    if (!text || loading) return;
    setLoading(true);
    const userTurn = { role: "user", content: text };
    const histBefore = history.slice(-20);
    setHistory((h) => [...h, userTurn]);
    setInput("");
    try {
      const r = await api.post("/my/seller-assistant/chat", {
        message: text,
        history: histBefore,
      });
      const msg = r.data.assistant_message || "Kész.";
      setHistory((h) => [...h, { role: "assistant", content: msg }]);
    } catch (e) {
      const detail = e.response?.data?.detail || "Az asszisztens most nem elérhető.";
      toast.error(detail);
      setHistory((h) => [...h, { role: "assistant", content: detail }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="seller-assistant-panel"
      className={
        embedded
          ? "flex flex-col flex-1 min-h-0 h-full"
          : "glass rounded-3xl border border-white/10 flex flex-col min-h-[520px] h-full flex-1"
      }
    >
      <div className="px-5 py-4 border-b border-white/10 flex items-start gap-3 shrink-0">
        <div className="w-10 h-10 rounded-xl bg-violet-500/20 flex items-center justify-center shrink-0">
          <Bot className="w-5 h-5 text-violet-300" strokeWidth={1.5} />
        </div>
        <div className="min-w-0">
          <h2 className="font-heading text-lg font-light text-white flex items-center gap-2">
            Eladói asszisztens
            <Sparkles className="w-4 h-4 text-violet-400" strokeWidth={1.5} />
          </h2>
          <p className="text-xs text-slate-400 mt-1 leading-relaxed">
            Kérdezz az AI ügyfélszolgálatról, a briefről, a hirdetéseidről és a kreditekről — itt segítünk
            beállítani, a vevők chatje külön az Üzeneteknél van.
          </p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-0">
        {history.length === 0 && (
          <div className="space-y-3">
            <p className="text-xs text-slate-500">Gyors kérdések:</p>
            <div className="flex flex-wrap gap-2">
              {STARTERS.map((s) => (
                <button
                  key={s}
                  type="button"
                  data-testid="seller-assistant-starter"
                  onClick={() => send(s)}
                  className="text-left text-xs px-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-slate-300 hover:border-violet-400/40 hover:text-white transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {history.map((m, i) => (
          <div
            key={i}
            className={`rounded-2xl px-3.5 py-2.5 text-sm whitespace-pre-wrap leading-relaxed max-w-[92%] ${
              m.role === "user"
                ? "bg-electric/15 border border-electric/25 ml-auto text-slate-100"
                : "bg-violet-500/10 border border-violet-400/20 mr-auto text-slate-200"
            }`}
          >
            {m.content}
          </div>
        ))}
        {loading && (
          <div className="flex items-center gap-2 text-slate-400 text-xs">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Gondolkodom…
          </div>
        )}
        <div ref={endRef} />
      </div>

      <form
        className="p-4 border-t border-white/10 shrink-0"
        onSubmit={(e) => {
          e.preventDefault();
          send();
        }}
      >
        <div className="flex gap-2">
          <input
            data-testid="seller-assistant-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Írj az asszisztensnek…"
            className="flex-1 rounded-full bg-white/[0.04] border border-white/10 px-4 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-violet-400/50"
            disabled={loading}
          />
          <button
            type="submit"
            data-testid="seller-assistant-send"
            disabled={loading || !input.trim()}
            className="shrink-0 w-11 h-11 rounded-full bg-violet-600 hover:bg-violet-500 disabled:opacity-40 flex items-center justify-center text-white transition-colors"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </form>
    </div>
  );
}
